import { Component, inject, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { AuditService } from '@services/audit/audit.service';
import { AuditEvent, AuditEventsListParams } from '@interfaces/audit';
import { NzTableModule, NzTableQueryParams } from 'ng-zorro-antd/table';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzMessageService } from 'ng-zorro-antd/message';
import { DatePipe } from '@angular/common';

const PAGE_SIZE = 50;
const ORIGIN_OPTIONS = [
  { value: '' as const, label: 'All' },
  { value: 'webapp' as const, label: 'Webapp' },
  { value: 'mobile' as const, label: 'Mobile' },
];

@Component({
  selector: 'app-audit-dashboard',
  standalone: true,
  imports: [
    FormsModule,
    NzTableModule,
    NzButtonModule,
    NzInputModule,
    NzSelectModule,
    NzDatePickerModule,
    NzDividerModule,
    NzIconModule,
    DatePipe,
  ],
  templateUrl: './audit-dashboard.component.html',
  styleUrl: './audit-dashboard.component.scss',
})
export class AuditDashboardComponent implements OnInit {
  private readonly auditService = inject(AuditService);
  private readonly message = inject(NzMessageService);

  loading = signal(false);
  events = signal<AuditEvent[]>([]);
  total = signal(0);
  pageIndex = signal(1);
  pageSize = signal(PAGE_SIZE);

  filterAction = '';
  filterOrigin: 'webapp' | 'mobile' | '' = '';
  filterEntityType = '';
  filterEntityId = '';
  filterActorId = '';
  filterFrom: Date | null = null;
  filterTo: Date | null = null;

  originOptions = ORIGIN_OPTIONS;

  cleanupLoading = signal(false);
  cleanupDays = 90;

  ngOnInit(): void {
    this.loadEvents();
  }

  private buildParams(): AuditEventsListParams {
    const limit = this.pageSize();
    const offset = (this.pageIndex() - 1) * limit;
    const params: AuditEventsListParams = { limit, offset };
    if (this.filterAction?.trim()) params.action = this.filterAction.trim();
    if (this.filterOrigin === 'webapp' || this.filterOrigin === 'mobile') params.origin = this.filterOrigin;
    if (this.filterEntityType?.trim()) params.entity_type = this.filterEntityType.trim();
    if (this.filterEntityId?.trim()) params.entity_id = this.filterEntityId.trim();
    if (this.filterActorId?.trim()) params.actor_id = this.filterActorId.trim();
    if (this.filterFrom) params.from = this.filterFrom.toISOString().slice(0, 10);
    if (this.filterTo) params.to = this.filterTo.toISOString().slice(0, 10);
    return params;
  }

  loadEvents(): void {
    const params = this.buildParams();
    this.loading.set(true);
    this.auditService.list(params).subscribe({
      next: (res) => {
        this.events.set(res.events);
        this.total.set(res.total);
        this.loading.set(false);
      },
      error: () => {
        this.loading.set(false);
        this.message.error('Failed to load audit events');
      },
    });
  }

  applyFilters(): void {
    this.pageIndex.set(1);
    this.loadEvents();
  }

  resetFilters(): void {
    this.filterAction = '';
    this.filterOrigin = '';
    this.filterEntityType = '';
    this.filterEntityId = '';
    this.filterActorId = '';
    this.filterFrom = null;
    this.filterTo = null;
    this.pageIndex.set(1);
    this.loadEvents();
  }

  onQueryParamsChange(params: NzTableQueryParams): void {
    const { pageIndex, pageSize } = params;
    this.pageIndex.set(pageIndex ?? 1);
    this.pageSize.set(pageSize ?? PAGE_SIZE);
    this.loadEvents();
  }

  runCleanup(): void {
    this.cleanupLoading.set(true);
    this.auditService.cleanup(this.cleanupDays).subscribe({
      next: (res) => {
        this.cleanupLoading.set(false);
        this.message.success(`${res.message} (${res.deleted} deleted)`);
        this.loadEvents();
      },
      error: () => {
        this.cleanupLoading.set(false);
        this.message.error('Cleanup failed');
      },
    });
  }

  formatExtra(extra: Record<string, unknown> | null): string {
    if (extra == null) return '—';
    try {
      return JSON.stringify(extra);
    } catch {
      return '—';
    }
  }

  copyMessage(val: string) {
    const selBox = document.createElement('textarea');
    selBox.style.position = 'fixed';
    selBox.style.left = '0';
    selBox.style.top = '0';
    selBox.style.opacity = '0';
    selBox.value = val;
    document.body.appendChild(selBox);
    selBox.focus();
    selBox.select();
    document.execCommand('copy');
    document.body.removeChild(selBox);
  }
}
